import { useMutation } from '@tanstack/react-query';

import { checkoutApi } from '../../lib';

import type { CreateCartData, AddToCartData } from '../../lib/apiClient'
import type { AddToCartMutationInput, UserError } from '../../types';

/**
 * Extract user error message from unwrapped AddToCart response data
 */
function extractErrorMessage(data: AddToCartData): string | null {
  if (data?.addProductsToCart?.user_errors?.length) {
    return data.addProductsToCart.user_errors
      .map((error: UserError) => error.message)
      .join(', ');
  }

  return null;
}

interface UseCartMutationsProps {
  onCartCreated: (cartId: string | null) => void;
  onAddToCartSuccess: () => void;
  onError: (message: string | null) => void;
  setAddingToCart: (sku: string | null) => void;
}

/**
 * Hook for cart mutation operations
 */
export function useCartMutations({
  onCartCreated,
  onAddToCartSuccess,
  onError,
  setAddingToCart,
}: UseCartMutationsProps) {
  /**
   * Create guest cart mutation
   */
  const createCartMutation = useMutation({
    mutationKey: ['checkout', 'createGuestCart'],
    mutationFn: () => checkoutApi.createGuestCart(),
    onSuccess: (data: CreateCartData) => {
      const newCartId = data?.createEmptyCart ?? null;
      onCartCreated(newCartId);
    },
    onError: (error: Error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to create guest cart:', error);
      setAddingToCart(null);
      onError('Failed to create cart. Please try again.');
    },
  });

  /**
   * Add to cart mutation
   */
  const addProductMutation = useMutation({
    mutationKey: ['checkout', 'addToCart'],
    mutationFn: ({ cartId, data }: AddToCartMutationInput) =>
      checkoutApi.addToCart(cartId, [data]),
    onSuccess: (data: AddToCartData) => {
      setAddingToCart(null);

      const errorMsg = extractErrorMessage(data);
      onError(errorMsg);

      if (!errorMsg) {
        onAddToCartSuccess();
      }
    },
    onError: (error: Error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to add to cart:', error);
      setAddingToCart(null);
      onError('Failed to add product to cart. Please try again.');
    },
  });

  return {
    createGuestCart: createCartMutation.mutate,
    addToCart: addProductMutation.mutate,
    isCreatingCart: createCartMutation.isPending,
  };
}
